import { useEffect, useRef, useState } from 'react'
import { dia, shapes, fitContent, onSized } from '../../lib/joint'
import type { ContactView } from '../../types/contacts'
import { PERSONA_GLYPH, PERSONA_WORDS } from './stages'

// The influence map of ONE account: the company at the centre, its people in rings by persona (the ones
// who sign and pull the work in closest, the users outermost), each node sized by relationship strength
// and coloured by role; the firm on the left with the colleagues who cover the account, tied to the people
// they know. Drag to rearrange, click a person to open them, hover to read the tie. Built on the shared
// JointJS interop in lib/joint (not relitigated here); colours come from the skin's tokens at build time.

export interface LeadMapProps {
  contacts: ContactView[]
  companyName: string
  firmName: string
  /** the firm members who cover the account, by id, with the name to show */
  coverage: { ownerId: string; name: string }[]
  selected: string | null
  personaFilter: string | null
  onOpen: (personId: string) => void
}

const RING: Record<string, number> = { 'decision-maker': 0, champion: 0, influencer: 1, challenger: 1, gatekeeper: 1, user: 2, other: 2 }

interface MapNode { cell: any; persona: string; quiet: boolean; links: { cell: any; base: number; stroke: string }[] }

function token(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}

export function LeadMap({ contacts, companyName, firmName, coverage, selected, personaFilter, onOpen }: LeadMapProps) {
  const hostRef = useRef<HTMLDivElement>(null)
  const paperRef = useRef<HTMLDivElement>(null)
  const [zoom, setZoom] = useState(100)
  const controls = useRef<{ fit: () => void; zoom: (f: number) => void } | null>(null)
  const nodes = useRef<Map<string, MapNode>>(new Map())
  const openRef = useRef(onOpen)
  openRef.current = onOpen

  useEffect(() => {
    const host = hostRef.current; const el = paperRef.current
    if (!host || !el) return
    let paper: any = null
    const c = {
      ink: token('--ink'), ink2: token('--ink-2'), muted: token('--muted'), line: token('--line'), surface: token('--surface'),
      brand: token('--brand'), brandStrong: token('--brand-strong'),
      role: (r: string) => token(`--role-${r}`) || token('--role-other'),
    }

    const build = (w: number, h: number) => {
      // the paper owns a child mount: paper.remove() takes its element with it, and the host must survive a rebuild
      const mount = document.createElement('div')
      mount.className = 'lead-map-mount'
      el.replaceChildren(mount)
      const model = new dia.Graph({}, { cellNamespace: shapes })
      nodes.current = new Map()
      const cx = w * 0.58; const cy = h / 2
      const R = Math.min(w * 0.42, h * 0.46)

      // the company
      const company = new shapes.standard.Rectangle({
        position: { x: cx - 84, y: cy - 24 }, size: { width: 168, height: 48 },
        attrs: { body: { fill: c.ink, stroke: c.ink, rx: 10, ry: 10 }, label: { text: companyName, fill: c.surface, fontSize: 13, fontWeight: 700, textWrap: { width: 156, maxLineCount: 2, ellipsis: true } } },
      })
      company.addTo(model)

      // the account's people, in rings by persona, spread over the right-hand arc
      const people = contacts.filter((x) => x.person.role !== 'colleague')
      const rings: ContactView[][] = [[], [], []]
      for (const p of people) rings[RING[p.person.persona ?? 'other'] ?? 2].push(p)
      const radii = [R * 0.42, R * 0.72, R * 1.0]
      rings.forEach((ring, ri) => {
        const n = ring.length
        ring.forEach((x, i) => {
          const sweep = Math.PI * (n <= 2 ? 0.9 : 1.45)
          const a = n === 1 ? 0 : -sweep / 2 + (i / (n - 1)) * sweep
          const px = cx + Math.cos(a) * radii[ri]; const py = cy + Math.sin(a) * radii[ri]
          const strength = x.person.relationshipStrength ?? 0
          const d = 20 + Math.round(strength * 28)
          const role = x.person.role ?? 'other'
          const persona = x.person.persona ?? 'other'
          const quiet = x.signals.some((s) => s.type === 'dormant')
          const moved = x.signals.some((s) => s.type === 'job-change')
          const node = new shapes.standard.Circle({
            position: { x: px - d / 2, y: py - d / 2 }, size: { width: d, height: d },
            markup: [{ tagName: 'circle', selector: 'body' }, { tagName: 'text', selector: 'glyph' }, { tagName: 'text', selector: 'label' }],
            attrs: {
              body: { fill: c.role(role), stroke: c.surface, strokeWidth: 2, cx: 'calc(w/2)', cy: 'calc(h/2)', r: 'calc(w/2)', strokeDasharray: moved ? '4 3' : 'none', opacity: quiet ? 0.55 : 1 },
              glyph: { text: PERSONA_GLYPH[persona] ?? '', fill: c.surface, fontSize: Math.max(9, d - 12), fontWeight: 700, x: 'calc(w/2)', y: 'calc(h/2)', textAnchor: 'middle', textVerticalAnchor: 'middle' },
              label: { text: x.person.name, fill: c.ink2, fontSize: 11, x: 'calc(w/2)', y: 'calc(h+5)', textAnchor: 'middle', textVerticalAnchor: 'top', textWrap: { width: 120, maxLineCount: 1, ellipsis: true } },
            },
          })
          ;(node as any).personId = x.person.personId
          ;(node as any).title = `${x.person.name}${x.person.title ? ' · ' + x.person.title : ''} · ${PERSONA_WORDS[persona] ?? persona} · ${role} · ${Math.round(strength * 100)}% tie${quiet ? ' · gone quiet' : ''}${moved ? ' · may have moved' : ''}`
          node.addTo(model)
          const tie = new shapes.standard.Link({
            source: { id: company.id }, target: { id: node.id }, z: -1,
            attrs: { line: { stroke: c.line, strokeWidth: 1 + strength * 3, targetMarker: { type: 'none' } } },
          })
          tie.addTo(model)
          nodes.current.set(x.person.personId, { cell: node, persona, quiet, links: [{ cell: tie, base: 1, stroke: c.line }] })
        })
      })

      // the firm and the colleagues who cover the account, tied to the people they know
      const fx = Math.max(60, cx - R - 150)
      const firm = new shapes.standard.Circle({
        position: { x: fx - 34, y: cy - 34 }, size: { width: 68, height: 68 },
        attrs: { body: { fill: c.brandStrong, stroke: c.brandStrong }, label: { text: firmName, fill: c.surface, fontSize: 11, fontWeight: 700, textWrap: { width: 60, maxLineCount: 2, ellipsis: true } } },
      })
      firm.addTo(model)
      const owners = coverage.slice(0, 8)
      owners.forEach((o, i) => {
        const oy = cy + (i - (owners.length - 1) / 2) * 56
        const ox = fx + 110
        const node = new shapes.standard.Circle({
          position: { x: ox - 11, y: oy - 11 }, size: { width: 22, height: 22 },
          attrs: { body: { fill: c.role('colleague'), stroke: c.surface, strokeWidth: 2 }, label: { text: o.name, fill: c.ink2, fontSize: 11, x: 'calc(w/2)', y: 'calc(h+4)', textAnchor: 'middle', textVerticalAnchor: 'top', textWrap: { width: 110, maxLineCount: 1, ellipsis: true } } },
        })
        ;(node as any).title = `${o.name} · covers this account`
        node.addTo(model)
        new shapes.standard.Link({ source: { id: firm.id }, target: { id: node.id }, z: -1, attrs: { line: { stroke: c.line, strokeWidth: 1.2, targetMarker: { type: 'none' } } } }).addTo(model)
        for (const p of people.filter((x) => x.person.ownerId === o.ownerId)) {
          const entry = nodes.current.get(p.person.personId)
          if (!entry) continue
          const link = new shapes.standard.Link({
            source: { id: node.id }, target: { id: entry.cell.id }, z: -2,
            attrs: { line: { stroke: c.brand, strokeWidth: 1, strokeDasharray: '3 4', opacity: 0.55, targetMarker: { type: 'none' } } },
          })
          link.addTo(model)
          entry.links.push({ cell: link, base: 0.55, stroke: c.brand })
        }
      })

      paper = new dia.Paper({ el: mount, model, width: w, height: h, gridSize: 1, interactive: { linkMove: false }, background: { color: 'transparent' }, cellViewNamespace: shapes })
      paper.on('element:pointerclick', (view: any) => { const id = view.model.personId; if (id) openRef.current(id) })
      paper.on('element:mouseenter', (view: any) => {
        const t = view.model.title; if (t) view.el.setAttribute('title', t)
        const id = view.model.personId; const entry = id ? nodes.current.get(id) : null
        entry?.links.forEach((l) => { l.cell.attr('line/stroke', c.brand); l.cell.attr('line/opacity', 1) })
      })
      paper.on('element:mouseleave', (view: any) => {
        const id = view.model.personId; const entry = id ? nodes.current.get(id) : null
        entry?.links.forEach((l) => { l.cell.attr('line/stroke', l.stroke); l.cell.attr('line/opacity', l.base) })
      })
      fitContent(paper, model, w, h, 36)
      setZoom(Math.round(paper.scale().sx * 100))
      controls.current = {
        fit: () => { fitContent(paper, model, w, h, 36); setZoom(Math.round(paper.scale().sx * 100)) },
        zoom: (f) => { const s = Math.max(0.2, Math.min(3, paper.scale().sx * f)); paper.scale(s); setZoom(Math.round(s * 100)) },
      }
      applyEmphasis(nodes.current, selected, personaFilter, c.brand, c.surface)
    }
    const disconnect = onSized(host, build, (w, h) => { if (paper) { paper.setDimensions(w, h); fitContent(paper, paper.model, w, h, 36); setZoom(Math.round(paper.scale().sx * 100)) } })
    return () => { disconnect(); if (paper) paper.remove() }
    // selection and filter are applied without a rebuild (below) so a dragged layout survives them
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, companyName, firmName, coverage])

  useEffect(() => {
    applyEmphasis(nodes.current, selected, personaFilter, token('--brand'), token('--surface'))
  }, [selected, personaFilter])

  return (
    <div className="lead-map" ref={hostRef}>
      <div className="lead-map-paper" ref={paperRef} />
      <div className="lead-map-controls">
        <button className="button small" onClick={() => controls.current?.zoom(1.2)} aria-label="Zoom in">+</button>
        <button className="button small" onClick={() => controls.current?.zoom(1 / 1.2)} aria-label="Zoom out">−</button>
        <button className="button small" onClick={() => controls.current?.fit()}>Fit</button>
        <span className="muted small">{zoom}%</span>
      </div>
    </div>
  )
}

function applyEmphasis(nodes: Map<string, MapNode>, selected: string | null, personaFilter: string | null, brand: string, surface: string) {
  for (const [id, n] of nodes) {
    const dim = personaFilter != null && n.persona !== personaFilter
    const on = selected === id
    n.cell.attr('body/stroke', on ? brand : surface)
    n.cell.attr('body/strokeWidth', on ? 4 : 2)
    n.cell.attr('body/opacity', dim ? 0.2 : n.quiet ? 0.55 : 1)
    n.cell.attr('glyph/opacity', dim ? 0.2 : 1)
    n.cell.attr('label/opacity', dim ? 0.35 : 1)
    n.links.forEach((l) => l.cell.attr('line/opacity', dim ? 0.12 : on ? 1 : l.base))
  }
}
